import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { billingSourceLabel } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Crown, Check, Loader2, Info, Smartphone, Sparkles, ExternalLink, Lock } from "lucide-react";

const PERKS = [
  "Unlimited interests & likes",
  "View contact details of your matches",
  "Direct Wali / Guardian access",
  "Advanced deen & lifestyle filters",
  "AI compatibility insights & coach",
  "Priority visibility",
];

const COMPARE = [
  { label: "Browse & discover profiles", free: true, premium: true },
  { label: "Express interest", free: "Limited", premium: "Unlimited" },
  { label: "View match contact details", free: false, premium: true },
  { label: "Wali / guardian access", free: false, premium: true },
  { label: "Advanced deen & lifestyle filters", free: false, premium: true },
  { label: "AI compatibility insights & coach", free: false, premium: true },
  { label: "Priority visibility", free: false, premium: true },
];

function Cell({ v }) {
  if (v === true) return <Check className="mx-auto h-4 w-4 text-emerald-600" />;
  if (v === false) return <span className="mx-auto block h-0.5 w-3 rounded bg-slate-300" />;
  return <span className="text-xs font-semibold text-emerald-800">{v}</span>;
}

export default function Premium() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [selected, setSelected] = useState("annual");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/premium/plans"); setInfo(data); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    })();
  }, []);

  // Show a message when the user returns from Stripe having cancelled checkout.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("checkout") === "cancelled" || params.get("checkout") === "canceled") {
      toast("Checkout cancelled — you have not been charged.");
      navigate("/premium", { replace: true });
    }
  }, [location.search, navigate]);

  const webAvailable = info?.webCheckout?.available;
  const perks = info?.features?.length ? info.features : PERKS;
  const selectedPlan = (info?.plans || []).find((p) => p.id === selected);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan: selected, origin_url: window.location.origin });
      if (data.checkout_url || data.url) window.location.href = data.checkout_url || data.url;
      else toast.error("Checkout could not be started.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data.url || data.portal_url) window.location.href = data.url || data.portal_url;
      else toast.error("Could not open billing management.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPortalLoading(false); }
  };

  if (user?.is_premium) {
    const sub = user.subscription || {};
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-10 text-center shadow-sm" data-testid="premium-active">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500 text-white"><Crown className="h-8 w-8" /></div>
          <h1 className="font-serif text-3xl font-bold text-emerald-950">You're Premium</h1>
          <p className="mt-2 text-slate-600">Your Premium is recognized here from your existing account — no need to buy again.</p>
          <div className="mt-6 rounded-2xl bg-white p-4 text-left text-sm text-slate-700 shadow-sm">
            <p className="flex items-center gap-2"><Info className="h-4 w-4 text-emerald-700" /> <b>Billing source:</b> {billingSourceLabel(sub.source)}</p>
            {sub.plan && <p className="mt-1 pl-6 text-slate-500">Plan: {sub.plan}{sub.lifetime ? " (lifetime)" : ""}</p>}
            {sub.reason && <p className="mt-1 pl-6 text-slate-500">{sub.reason}</p>}
            {sub.current_period_end && <p className="mt-1 pl-6 text-slate-500">Renews / expires: {new Date(sub.current_period_end).toLocaleDateString()}</p>}
            {sub.source === "stripe" ? (
              <p className="mt-2 flex items-center gap-2 text-slate-500"><Info className="h-4 w-4" /> Manage or cancel your subscription anytime from the billing portal.</p>
            ) : (
              <p className="mt-2 flex items-center gap-2 text-slate-500"><Smartphone className="h-4 w-4" /> Manage or cancel in the app store you purchased from (Google Play / Apple).</p>
            )}
          </div>
          {sub.source === "stripe" && (
            <Button onClick={openBillingPortal} disabled={portalLoading} data-testid="manage-billing-btn" className="mt-6 rounded-full bg-emerald-800 text-white hover:bg-emerald-900">
              {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Manage billing <ExternalLink className="ml-2 h-4 w-4" /></>}
            </Button>
          )}
          <div>
            <Button onClick={() => navigate("/discovery")} variant={sub.source === "stripe" ? "outline" : "default"} className={`mt-3 rounded-full ${sub.source === "stripe" ? "border-emerald-800/30 text-emerald-900 hover:bg-mint" : "bg-emerald-800 text-white hover:bg-emerald-900"}`}>Back to discovery</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      {/* Hero */}
      <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-800 to-emerald-950 p-8 text-center text-white shadow-lg sm:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-950"><Crown className="h-3.5 w-3.5" /> Premium</span>
        <h1 className="mt-4 font-serif text-4xl font-bold sm:text-5xl">Unlock meaningful connections</h1>
        <p className="mx-auto mt-3 max-w-xl text-emerald-100/85">Send unlimited interests, see who likes you, unlock your matches' contact and Wali details, and get AI compatibility insights — one membership that works across web, Android and iOS.</p>
      </div>

      {/* Plan selection */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {(info?.plans || []).map((plan) => {
          const active = selected === plan.id;
          return (
            <button key={plan.id} onClick={() => { setSelected(plan.id); setConfirming(false); }} data-testid={`plan-${plan.id}`}
              className={`relative rounded-3xl border-2 p-6 text-left transition-all ${active ? "border-amber-500 bg-amber-50/70 shadow-md" : "border-slate-200 bg-white hover:border-amber-300"}`}>
              {plan.badge && <span className="absolute -top-3 right-5 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">{plan.badge}</span>}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-serif text-2xl font-bold text-emerald-950"><Sparkles className={`h-5 w-5 ${active ? "text-amber-500" : "text-emerald-300"}`} /> {plan.name}</div>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${active ? "border-amber-500 bg-amber-500 text-white" : "border-slate-300"}`}>{active && <Check className="h-4 w-4" />}</span>
              </div>
              <p className="mt-2 text-sm text-slate-600"><b className="text-emerald-900">{plan.displayPrice}</b> / {plan.period}</p>
            </button>
          );
        })}
      </div>

      {webAvailable ? (
        <>
          {!confirming ? (
            <>
              <Button onClick={() => setConfirming(true)} data-testid="purchase-btn" className="mt-5 w-full rounded-full bg-amber-600 py-6 text-base font-semibold text-white hover:bg-amber-700">
                Get Premium — {selectedPlan?.displayPrice ? `${selectedPlan.displayPrice} / ${selectedPlan.period}` : (selected === "annual" ? "Annual" : "Monthly")}
              </Button>
              <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Secure payment powered by Stripe. Cancel anytime.</p>
            </>
          ) : (
            <div className="mt-5 rounded-3xl border-2 border-amber-500 bg-amber-50/70 p-6 text-center" data-testid="payment-confirm">
              <p className="font-serif text-xl font-bold text-emerald-950">Do you want to proceed with the payment?</p>
              <p className="mt-1.5 text-sm text-slate-600">
                {selectedPlan?.name || (selected === "annual" ? "Annual" : "Monthly")} Premium
                {selectedPlan?.displayPrice ? ` — ${selectedPlan.displayPrice} / ${selectedPlan.period}` : ""}. You'll be taken to Stripe's secure page to finish.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button onClick={checkout} disabled={loading} data-testid="confirm-yes-btn" className="rounded-full bg-emerald-800 px-8 py-6 text-base font-semibold text-white hover:bg-emerald-900 sm:min-w-[160px]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Yes, proceed"}
                </Button>
                <Button onClick={() => setConfirming(false)} disabled={loading} variant="outline" data-testid="confirm-cancel-btn" className="rounded-full border-slate-300 px-8 py-6 text-base font-semibold text-slate-700 hover:bg-white sm:min-w-[160px]">
                  Cancel
                </Button>
              </div>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Secure payment powered by Stripe.</p>
            </div>
          )}
        </>
      ) : (
        <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="web-checkout-unavailable">
          <Smartphone className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
          <div><p className="text-sm font-semibold text-emerald-900">Subscribe in the Nikah Match app</p><p className="mt-0.5 text-xs text-slate-600">You can start your Premium membership in the Nikah Match Android or iOS app, and it unlocks here automatically across all your devices.</p></div>
        </div>
      )}

      {/* Comparison */}
      <div className="mt-8 overflow-hidden rounded-3xl border border-emerald-900/10 bg-white shadow-sm">
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-emerald-900/10 bg-mint/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-emerald-800">
          <span>What you get</span>
          <span className="w-16 text-center text-slate-500">Free</span>
          <span className="flex w-20 items-center justify-center gap-1 text-center text-amber-700"><Crown className="h-3.5 w-3.5" /> Premium</span>
        </div>
        {COMPARE.map((row) => (
          <div key={row.label} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-emerald-900/5 px-5 py-3 text-sm last:border-0">
            <span className="text-slate-700">{row.label}</span>
            <span className="flex w-16 justify-center"><Cell v={row.free} /></span>
            <span className="flex w-20 justify-center rounded-lg bg-amber-50/60 py-1"><Cell v={row.premium} /></span>
          </div>
        ))}
      </div>

      {/* Perks chips */}
      <div className="mt-6 rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
        <h3 className="font-serif text-xl font-semibold text-emerald-900">Everything in Premium</h3>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {perks.map((t, i) => <div key={i} className="flex items-center gap-2.5 text-slate-700"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-mint text-emerald-700"><Check className="h-4 w-4" /></span>{t}</div>)}
        </div>
      </div>
    </div>
  );
}
